import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import Header from "./Header";
import LoadingScreen from "./LoadingScreen";
import Notification from "./Notification";
import { FaBuilding, FaSortAlphaDown, FaSortAlphaUp, FaSortNumericDown, FaSortNumericUp } from "react-icons/fa";
import { FiEdit, FiTrash2, FiPlus, FiX } from "react-icons/fi";
import axios from "axios";
import Select from "react-select";

const CustomerManagement = () => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(() => (typeof window !== "undefined" ? window.innerWidth >= 1024 : true));
  const [customers, setCustomers] = useState([]);
  const [serviceCatalog, setServiceCatalog] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [isAddCustomerModalOpen, setIsAddCustomerModalOpen] = useState(false);
  const [isEditCustomerModalOpen, setIsEditCustomerModalOpen] = useState(false);
  const [isDeleteCustomerModalOpen, setIsDeleteCustomerModalOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [newCustomer, setNewCustomer] = useState({
    company_name: "",
    billing_account_id: "",
    maintenance: "5x8 Hours",
    limit_ticket: 10,
    cm_ticket_quota: 5,
    pm_frequency: "3_months",
    solution_domains: ["cloud", "network"],
    service_ids: [],
  });
  const [editCustomer, setEditCustomer] = useState({
    company_id: "",
    company_name: "",
    billing_account_id: "",
    maintenance: "5x8 Hours",
    limit_ticket: 10,
    cm_ticket_quota: 5,
    pm_frequency: "3_months",
    solution_domains: ["cloud"],
    service_ids: [],
  });
  const [sortBy, setSortBy] = useState("company_name");
  const [sortOrder, setSortOrder] = useState("asc"); // asc, desc
  const user = JSON.parse(localStorage.getItem("user"));
  const role = user ? user.role : null;

  const fetchCustomers = async () => {
    setIsLoading(true);
    try {
      const customerResponse = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/customers/`);
      setCustomers(customerResponse.data);
    } catch (error) {
      console.error("Error fetching customers:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      await fetchCustomers();
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/managed-services/catalog`);
        setServiceCatalog(response.data);
      } catch (error) {
        console.error("Error fetching service catalog:", error);
      }
    };
    fetchInitialData();
  }, []);

  const closeNotification = () => setNotification(null);
  const handleAddCustomerChange = (e) => {
    const { name, value } = e.target;
    setNewCustomer((prevState) => ({
      ...prevState,
      [name]: name === "limit_ticket" ? Number.parseInt(value) || 0 : value,
    }));
  };
  const serviceOptions = serviceCatalog.map((service) => ({
    value: service.id,
    label: service.service_name,
  }));
  const handleServiceSelection = (selectedServices, setCustomer) => {
    setCustomer((previous) => ({
      ...previous,
      service_ids: (selectedServices || []).map((service) => service.value),
    }));
  };
  const selectedServiceOptions = (serviceIds) =>
    serviceOptions.filter((service) => serviceIds.includes(service.value));
  const serviceSelectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: "48px",
      borderColor: state.isFocused ? "#3b82f6" : "#d1d5db",
      boxShadow: state.isFocused ? "0 0 0 2px rgba(59, 130, 246, 0.2)" : "none",
      "&:hover": { borderColor: "#3b82f6" },
    }),
    multiValue: (base) => ({ ...base, backgroundColor: "#dbeafe", borderRadius: "6px" }),
    multiValueLabel: (base) => ({ ...base, color: "#1d4ed8", fontWeight: 500 }),
    multiValueRemove: (base) => ({ ...base, color: "#2563eb", ":hover": { backgroundColor: "#bfdbfe", color: "#1e3a8a" } }),
  };
  const handleAddCustomerSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      company_name: newCustomer.company_name,
      billing_account_id: newCustomer.billing_account_id || null,
      maintenance: newCustomer.maintenance || "5x8 Hours",
      limit_ticket: Number(newCustomer.limit_ticket) || 10,
      cm_ticket_quota: Number(newCustomer.cm_ticket_quota) || 5,
      pm_frequency: newCustomer.pm_frequency || "3_months",
      solution_domains: newCustomer.solution_domains || ["cloud"],
      service_ids: newCustomer.service_ids,
    };
    try {
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/customers/`, payload);
      setNotification({ type: "success", message: "Customer added successfully!" });
      await fetchCustomers();
      setIsAddCustomerModalOpen(false);
      setNewCustomer({
        company_name: "",
        billing_account_id: "",
        maintenance: "5x8 Hours",
        limit_ticket: 10,
        cm_ticket_quota: 5,
        pm_frequency: "3_months",
        solution_domains: ["cloud", "network"],
        service_ids: [],
      });
    } catch (error) {
      setNotification({ type: "error", message: error?.response?.data?.message || "Failed to add customer. Please try again." });
    }
  };
  const handleEditCustomerChange = (e) => {
    const { name, value } = e.target;
    setEditCustomer((prevState) => ({ ...prevState, [name]: value }));
  };
  const handleEditCustomer = async (customer) => {
    setEditCustomer({
      company_id: customer.company_id,
      company_name: customer.company_name,
      billing_account_id: customer.billing_account_id || "",
      maintenance: customer.maintenance,
      limit_ticket: customer.limit_ticket,
      cm_ticket_quota: customer.cm_ticket_quota || 5,
      pm_frequency: customer.pm_frequency || "3_months",
      solution_domains: customer.solution_domains || ["cloud"],
      service_ids: [],
    });
    setIsEditCustomerModalOpen(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/managed-services/customers/${customer.company_id}/services`,
      );
      setEditCustomer((previous) => ({
        ...previous,
        service_ids: response.data.map((service) => service.service_id),
      }));
    } catch (error) {
      console.error("Error fetching customer services:", error);
    }
  };
  const handleEditCustomerSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      company_name: editCustomer.company_name,
      billing_account_id: editCustomer.billing_account_id || null,
      maintenance: editCustomer.maintenance,
      limit_ticket: Number(editCustomer.limit_ticket) || 10,
      cm_ticket_quota: Number(editCustomer.cm_ticket_quota) || 5,
      pm_frequency: editCustomer.pm_frequency || "3_months",
      solution_domains: editCustomer.solution_domains || ["cloud"],
      service_ids: editCustomer.service_ids,
    };
    try {
      await axios.put(`${process.env.REACT_APP_API_BASE_URL}/api/customers/${editCustomer.company_id}`, payload);
      setNotification({ type: "success", message: "Customer updated successfully!" });
      await fetchCustomers();
      setIsEditCustomerModalOpen(false);
    } catch (error) {
      setNotification({ type: "error", message: "Failed to update customer. Please try again." });
    }
  };
  const handleDeleteCustomer = async (company_id) => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/customers/${company_id}`);
      setNotification({ type: "success", message: "Customer deleted successfully!" });
      setCustomers(customers.filter((customer) => customer.company_id !== company_id));
    } catch (error) {
      setNotification({ type: "error", message: "Failed to delete customer. Please try again." });
    }
  };
  const openDeleteCustomerModal = (company_id) => {
    setCustomerToDelete(company_id);
    setIsDeleteCustomerModalOpen(true);
  };
  const confirmDeleteCustomer = () => {
    if (customerToDelete) {
      handleDeleteCustomer(customerToDelete);
      setIsDeleteCustomerModalOpen(false);
    }
  };
  const toggleSidebar = () => setSidebarOpen((prevState) => !prevState);
  const getSLABadgeColor = (sla) => {
    switch (sla) {
      case "7x24 Hours":
        return "bg-purple-100 text-purple-800 border-purple-200";
      case "5x8 Hours":
        return "bg-orange-100 text-orange-800 border-orange-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };
  // Sorting logic
  const sortedCustomers = [...customers].sort((a, b) => {
    if (sortBy === "company_name") {
      if (!a.company_name) return 1;
      if (!b.company_name) return -1;
      if (sortOrder === "asc") {
        return a.company_name.localeCompare(b.company_name);
      } else {
        return b.company_name.localeCompare(a.company_name);
      }
    } else if (sortBy === "billing_account_id") {
      if (!a.billing_account_id) return 1;
      if (!b.billing_account_id) return -1;
      if (sortOrder === "asc") {
        return a.billing_account_id.localeCompare(b.billing_account_id, undefined, { numeric: true });
      } else {
        return b.billing_account_id.localeCompare(a.billing_account_id, undefined, { numeric: true });
      }
    } else if (sortBy === "maintenance") {
      if (!a.maintenance) return 1;
      if (!b.maintenance) return -1;
      if (sortOrder === "asc") {
        return a.maintenance.localeCompare(b.maintenance);
      } else {
        return b.maintenance.localeCompare(a.maintenance);
      }
    } else if (sortBy === "limit_ticket") {
      if (sortOrder === "asc") {
        return (a.limit_ticket || 0) - (b.limit_ticket || 0);
      } else {
        return (b.limit_ticket || 0) - (a.limit_ticket || 0);
      }
    } else if (sortBy === "ticket_usage") {
      if (sortOrder === "asc") {
        return (a.ticket_usage || 0) - (b.ticket_usage || 0);
      } else {
        return (b.ticket_usage || 0) - (a.ticket_usage || 0);
      }
    }
    return 0;
  });
  if (isLoading) {
    return (
      <div className="flex flex-col min-h-screen">
        <Header toggleSidebar={toggleSidebar} sidebarOpen={sidebarOpen} />
        <div className="flex flex-1 bg-gray-100 min-w-0 max-w-full overflow-x-hidden">
          <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
          <div className={`flex-1 min-w-0 max-w-full overflow-x-hidden p-3 sm:p-6 transition-all duration-300 ${sidebarOpen ? "lg:ml-64" : "lg:ml-20"} ml-0`}>
            <div className="pt-20 pb-6">
              <div className="mb-8">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 sm:px-6 py-4 border-b border-gray-200">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                      <div className="flex items-center">
                        <div className="bg-blue-100 p-2 rounded-lg mr-3">
                          <FaBuilding className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <h2 className="text-xl font-semibold text-gray-900">Customer Management</h2>
                          <p className="text-sm text-gray-600">Manage customer accounts and billing information</p>
                        </div>
                      </div>
                      <button className="flex items-center justify-center bg-blue-600 text-white py-2 px-4 rounded-lg shadow-sm" disabled>
                        <FiPlus className="mr-2 w-4 h-4" />
                        Add Customer
                      </button>
                    </div>
                  </div>
                  <div className="overflow-x-auto w-full">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Billing ID</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SLA</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ticket Limit</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usage</th>
                          <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                    </table>
                    <div className="bg-white">
                      <div className="divide-y divide-gray-200">
                        {[...Array(5)].map((_, index) => (
                          <div key={index} className="flex">
                            <div className="px-6 py-4 w-1/6">
                              <div className="h-4 bg-gray-200 rounded w-24 animate-pulse"></div>
                            </div>
                            <div className="px-6 py-4 w-1/6">
                              <div className="h-4 bg-gray-200 rounded w-20 animate-pulse"></div>
                            </div>
                            <div className="px-6 py-4 w-1/6">
                              <div className="h-4 bg-gray-200 rounded w-20 animate-pulse"></div>
                            </div>
                            <div className="px-6 py-4 w-1/6">
                              <div className="h-4 bg-gray-200 rounded w-16 animate-pulse"></div>
                            </div>
                            <div className="px-6 py-4 w-1/6">
                              <div className="h-4 bg-gray-200 rounded w-16 animate-pulse mb-2"></div>
                              <div className="h-2 bg-gray-100 rounded w-20 animate-pulse"></div>
                            </div>
                            <div className="px-6 py-4 w-1/6 flex justify-center">
                              <div className="h-8 bg-gray-200 rounded w-16 animate-pulse"></div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="flex flex-col min-h-screen">
      <Header toggleSidebar={toggleSidebar} sidebarOpen={sidebarOpen} />
      <div className="flex flex-1 bg-gray-100 min-w-0 max-w-full overflow-x-hidden">
        <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
        <div className={`flex-1 min-w-0 max-w-full overflow-x-hidden p-3 sm:p-6 transition-all duration-300 ${sidebarOpen ? "lg:ml-64" : "lg:ml-20"} ml-0`}>
          <div className="pt-20 pb-6">
            <div className="mb-8">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 sm:px-6 py-4 border-b border-gray-200">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                    <div className="flex items-center">
                      <div className="bg-blue-100 p-2 rounded-lg mr-3">
                        <FaBuilding className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <h2 className="text-xl font-semibold text-gray-900">Customer Management</h2>
                        <p className="text-sm text-gray-600">Manage customer accounts and billing information</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setIsAddCustomerModalOpen(true)}
                      className="flex items-center justify-center bg-blue-600 text-white py-2 px-4 rounded-lg shadow-sm hover:bg-blue-700 transition-all duration-200 hover:shadow-md"
                    >
                      <FiPlus className="mr-2 w-4 h-4" />
                      Add Customer
                    </button>
                  </div>
                </div>
                <div className="overflow-x-auto w-full">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <div className="flex items-center space-x-1">
                            Company
                            <button
                              className="ml-1 text-gray-400 hover:text-blue-600"
                              onClick={() => {
                                setSortBy("company_name");
                                setSortOrder(sortBy === "company_name" && sortOrder === "asc" ? "desc" : "asc");
                              }}
                            >
                              {sortBy === "company_name" && sortOrder === "asc" ? (
                                <FaSortAlphaDown className="inline w-4 h-4" />
                              ) : (
                                <FaSortAlphaUp className="inline w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <div className="flex items-center space-x-1">
                            Billing ID
                            <button
                              className="ml-1 text-gray-400 hover:text-blue-600"
                              onClick={() => {
                                setSortBy("billing_account_id");
                                setSortOrder(sortBy === "billing_account_id" && sortOrder === "asc" ? "desc" : "asc");
                              }}
                            >
                              {sortBy === "billing_account_id" && sortOrder === "asc" ? (
                                <FaSortNumericDown className="inline w-4 h-4" />
                              ) : (
                                <FaSortNumericUp className="inline w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <div className="flex items-center space-x-1">
                            SLA
                            <button
                              className="ml-1 text-gray-400 hover:text-blue-600"
                              onClick={() => {
                                setSortBy("maintenance");
                                setSortOrder(sortBy === "maintenance" && sortOrder === "asc" ? "desc" : "asc");
                              }}
                            >
                              {sortBy === "maintenance" && sortOrder === "asc" ? (
                                <FaSortAlphaDown className="inline w-4 h-4" />
                              ) : (
                                <FaSortAlphaUp className="inline w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <div className="flex items-center space-x-1">
                            Ticket Limit
                            <button
                              className="ml-1 text-gray-400 hover:text-blue-600"
                              onClick={() => {
                                setSortBy("limit_ticket");
                                setSortOrder(sortBy === "limit_ticket" && sortOrder === "asc" ? "desc" : "asc");
                              }}
                            >
                              {sortBy === "limit_ticket" && sortOrder === "asc" ? (
                                <FaSortNumericDown className="inline w-4 h-4" />
                              ) : (
                                <FaSortNumericUp className="inline w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <div className="flex items-center space-x-1">
                            Usage
                            <button
                              className="ml-1 text-gray-400 hover:text-blue-600"
                              onClick={() => {
                                setSortBy("ticket_usage");
                                setSortOrder(sortBy === "ticket_usage" && sortOrder === "asc" ? "desc" : "asc");
                              }}
                            >
                              {sortBy === "ticket_usage" && sortOrder === "asc" ? (
                                <FaSortNumericDown className="inline w-4 h-4" />
                              ) : (
                                <FaSortNumericUp className="inline w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {sortedCustomers.map((customer) => (
                        <tr
                          key={customer.company_id || customer.billing_account_id || customer.company_name}
                          onClick={() => navigate(`/customer-management/${customer.company_id}`)}
                          className="hover:bg-blue-50/60 transition-colors cursor-pointer group"
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                              {customer.company_name}
                            </div>
                            <div className="text-xs text-gray-500 font-mono">{customer.company_id}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="font-mono text-sm text-gray-600">{customer.billing_account_id || "-"}</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full border ${getSLABadgeColor(customer.maintenance)}`}>
                              {customer.maintenance}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{customer.limit_ticket}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <span className="text-sm text-gray-900 mr-2">{customer.ticket_usage || 0}</span>
                              <div className="w-16 bg-gray-200 rounded-full h-2">
                                <div
                                  className="bg-blue-600 h-2 rounded-full"
                                  style={{ width: `${Math.min(((customer.ticket_usage || 0) / (customer.limit_ticket || 1)) * 100, 100)}%` }}
                                ></div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center" onClick={(e) => e.stopPropagation()}>
                            <div className="flex justify-center space-x-2">
                              <button
                                className="text-blue-600 hover:text-blue-800 p-1.5 rounded-md hover:bg-blue-50 transition-colors"
                                title="Edit Customer"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditCustomer(customer);
                                }}
                              >
                                <FiEdit className="w-4 h-4" />
                              </button>
                              <button
                                className="text-red-600 hover:text-red-800 p-1.5 rounded-md hover:bg-red-50 transition-colors"
                                title="Delete Customer"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openDeleteCustomerModal(customer.company_id);
                                }}
                              >
                                <FiTrash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {notification && (<Notification type={notification.type} message={notification.message} onClose={closeNotification} />)}
      {isAddCustomerModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">Add New Customer</h3>
              <button
                onClick={() => setIsAddCustomerModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FiX className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleAddCustomerSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                <input
                  type="text"
                  name="company_name"
                  value={newCustomer.company_name}
                  onChange={handleAddCustomerChange}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Billing Account ID (Optional for GCP)</label>
                <input
                  type="text"
                  name="billing_account_id"
                  value={newCustomer.billing_account_id}
                  onChange={handleAddCustomerChange}
                  placeholder="e.g. 01E318-E504D7-XXXXXX"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="mt-1 text-xs text-gray-500">Hanya diisi jika customer menggunakan layanan Google Cloud Platform.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Solution Domains (Solusi SMG)</label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  {["network", "security", "cloud", "infrastructure"].map((dom) => {
                    const isChecked = (newCustomer.solution_domains || []).includes(dom);
                    return (
                      <label key={dom} className="flex items-center space-x-2 text-sm text-gray-700 cursor-pointer p-2 border rounded-lg hover:bg-gray-50">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => {
                            const cur = newCustomer.solution_domains || [];
                            const updated = e.target.checked ? [...cur, dom] : cur.filter((x) => x !== dom);
                            setNewCustomer((prev) => ({ ...prev, solution_domains: updated }));
                          }}
                          className="rounded text-blue-600 focus:ring-blue-500 h-4 w-4"
                        />
                        <span className="capitalize font-medium">{dom}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">CM Quota (Hands-on)</label>
                  <input
                    type="number"
                    name="cm_ticket_quota"
                    value={newCustomer.cm_ticket_quota}
                    onChange={handleAddCustomerChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    min="0"
                    required
                  />
                  <p className="mt-1 text-xs text-gray-500">Kuota tiket Critical/High.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">PM Cycle</label>
                  <select
                    name="pm_frequency"
                    value={newCustomer.pm_frequency}
                    onChange={handleAddCustomerChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="none">None / Tidak Ada</option>
                    <option value="3_months">Tiap 3 Bulan</option>
                    <option value="6_months">Tiap 6 Bulan</option>
                    <option value="yearly">Tiap 1 Tahun</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Service Level Agreement</label>
                <select
                  name="maintenance"
                  value={newCustomer.maintenance}
                  onChange={handleAddCustomerChange}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="5x8 Hours">5x8 Hours</option>
                  <option value="7x24 Hours">7x24 Hours</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tickets Limit / Year</label>
                <input
                  type="number"
                  name="limit_ticket"
                  value={newCustomer.limit_ticket}
                  onChange={handleAddCustomerChange}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  min="0"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Services Used</label>
                <Select
                  isMulti
                  closeMenuOnSelect={false}
                  options={serviceOptions}
                  value={selectedServiceOptions(newCustomer.service_ids)}
                  onChange={(selected) => handleServiceSelection(selected, setNewCustomer)}
                  placeholder="Select one or more services"
                  noOptionsMessage={() => "No services available"}
                  styles={serviceSelectStyles}
                />
                <p className="mt-1 text-xs text-gray-500">Click to select multiple services. Use × on a service to remove it.</p>
              </div>
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setIsAddCustomerModalOpen(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Add Customer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {isEditCustomerModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">Edit Customer</h3>
              <button
                onClick={() => setIsEditCustomerModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FiX className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleEditCustomerSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                <input
                  type="text"
                  name="company_name"
                  value={editCustomer.company_name}
                  onChange={handleEditCustomerChange}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Billing Account ID (Optional for GCP)</label>
                <input
                  type="text"
                  name="billing_account_id"
                  value={editCustomer.billing_account_id}
                  onChange={handleEditCustomerChange}
                  placeholder="e.g. 01E318-E504D7-XXXXXX"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Solution Domains (Solusi SMG)</label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  {["network", "security", "cloud", "infrastructure"].map((dom) => {
                    const isChecked = (editCustomer.solution_domains || []).includes(dom);
                    return (
                      <label key={dom} className="flex items-center space-x-2 text-sm text-gray-700 cursor-pointer p-2 border rounded-lg hover:bg-gray-50">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => {
                            const cur = editCustomer.solution_domains || [];
                            const updated = e.target.checked ? [...cur, dom] : cur.filter((x) => x !== dom);
                            setEditCustomer((prev) => ({ ...prev, solution_domains: updated }));
                          }}
                          className="rounded text-blue-600 focus:ring-blue-500 h-4 w-4"
                        />
                        <span className="capitalize font-medium">{dom}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">CM Quota (Hands-on)</label>
                  <input
                    type="number"
                    name="cm_ticket_quota"
                    value={editCustomer.cm_ticket_quota}
                    onChange={handleEditCustomerChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    min="0"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">PM Cycle</label>
                  <select
                    name="pm_frequency"
                    value={editCustomer.pm_frequency}
                    onChange={handleEditCustomerChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="none">None / Tidak Ada</option>
                    <option value="3_months">Tiap 3 Bulan</option>
                    <option value="6_months">Tiap 6 Bulan</option>
                    <option value="yearly">Tiap 1 Tahun</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Service Level Agreement</label>
                <select
                  name="maintenance"
                  value={editCustomer.maintenance}
                  onChange={handleEditCustomerChange}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="5x8 Hours">5x8 Hours</option>
                  <option value="7x24 Hours">7x24 Hours</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tickets Limit / Year</label>
                <input
                  type="number"
                  name="limit_ticket"
                  value={editCustomer.limit_ticket}
                  onChange={handleEditCustomerChange}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  min="0"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Services Used</label>
                <Select
                  isMulti
                  closeMenuOnSelect={false}
                  options={serviceOptions}
                  value={selectedServiceOptions(editCustomer.service_ids)}
                  onChange={(selected) => handleServiceSelection(selected, setEditCustomer)}
                  placeholder="Select one or more services"
                  noOptionsMessage={() => "No services available"}
                  styles={serviceSelectStyles}
                />
                <p className="mt-1 text-xs text-gray-500">Click to select multiple services. Removed services are retained as history only.</p>
              </div>
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setIsEditCustomerModalOpen(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Update Customer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {isDeleteCustomerModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Confirm Delete Customer</h3>
              <p className="text-sm text-gray-700 mb-6">Are you sure you want to delete this customer? This action cannot be undone.</p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setIsDeleteCustomerModalOpen(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteCustomer}
                  className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Delete Customer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default CustomerManagement;
